import React, { useState } from 'react';
import {
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  FileText,
  Activity,
  Layers,
  Sparkles,
  Lock,
  RefreshCw,
  Plus,
  Search,
  Filter,
  Eye,
  Sliders,
  Building2,
  Users,
  Award,
  Calendar,
  DollarSign,
  AlertOctagon,
  Clock,
  HelpCircle,
  FileSpreadsheet,
  FileCheck,
  Check,
  X,
  Briefcase,
  Zap,
} from 'lucide-react';
import { AppView, RiskCategory, RiskStatus, AssuranceType } from '../../types';
import { enterpriseRiskEngine } from '../../services/risk/enterpriseRiskEngine';

interface ChiefRiskOfficerCommandCenterProps {
  onNavigateToView: (view: AppView) => void;
}

export const ChiefRiskOfficerCommandCenter: React.FC<ChiefRiskOfficerCommandCenterProps> = ({
  onNavigateToView,
}) => {
  const [activeTab, setActiveTab] = useState<
    | 'overview'
    | 'register'
    | 'appetite_kri'
    | 'controls'
    | 'resilience'
    | 'fraud'
    | 'assurance'
    | 'insurance'
    | 'copilot'
  >('overview');

  // Engine State
  const [standardsRegistry] = useState(enterpriseRiskEngine.getStandardsRegistry());
  const [internalAuditProfile] = useState(enterpriseRiskEngine.getInternalAuditProfile());
  const [objectives] = useState(enterpriseRiskEngine.getObjectives());
  const [risks, setRisks] = useState(enterpriseRiskEngine.getRisks());
  const [appetiteStatements] = useState(enterpriseRiskEngine.getAppetiteStatements());
  const [appetiteBreaches, setAppetiteBreaches] = useState(enterpriseRiskEngine.getAppetiteBreaches());
  const [treatmentPlans] = useState(enterpriseRiskEngine.getTreatmentPlans());
  const [controls] = useState(enterpriseRiskEngine.getControls());
  const [controlTests, setControlTests] = useState(enterpriseRiskEngine.getControlTests());
  const [controlDeficiencies] = useState(enterpriseRiskEngine.getControlDeficiencies());
  const [issues] = useState(enterpriseRiskEngine.getIssues());
  const [kris] = useState(enterpriseRiskEngine.getKRIs());
  const [criticalServices] = useState(enterpriseRiskEngine.getCriticalBusinessServices());
  const [bias] = useState(enterpriseRiskEngine.getBIAs());
  const [resilienceExercises, setResilienceExercises] = useState(enterpriseRiskEngine.getResilienceExercises());
  const [fraudAssessments] = useState(enterpriseRiskEngine.getFraudRiskAssessments());
  const [fraudCases, setFraudCases] = useState(enterpriseRiskEngine.getFraudCases());
  const [speakUpCases] = useState(enterpriseRiskEngine.getSpeakUpCases());
  const [assuranceMap] = useState(enterpriseRiskEngine.getAssuranceMap());
  const [insurancePolicies] = useState(enterpriseRiskEngine.getInsurancePolicies());
  const [lossEvents] = useState(enterpriseRiskEngine.getLossEvents());
  const [nearMisses] = useState(enterpriseRiskEngine.getNearMisses());

  // Notification Toast
  const [notificationMsg, setNotificationMsg] = useState<string | null>(null);
  const triggerNotification = (msg: string) => {
    setNotificationMsg(msg);
    setTimeout(() => setNotificationMsg(null), 4000);
  };

  // Filter & Search
  const [searchFilter, setSearchFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');

  // New Risk Modal
  const [isNewRiskModalOpen, setIsNewRiskModalOpen] = useState(false);
  const [newRiskForm, setNewRiskForm] = useState({
    riskCode: `RSK-CUSTOM-${Math.floor(Math.random() * 900 + 100)}`,
    title: '',
    statementCause: '',
    statementEvent: '',
    statementImpact: '',
    objectiveId: objectives[0]?.id || 'OBJ-01',
    category: 'FINANCIAL_CORRECTNESS' as RiskCategory,
    owner: 'VP Engineering',
    affectedProducts: 'Financial Calculators & APIs',
    affectedMarkets: 'GLOBAL',
    inherentLikelihood: 4,
    inherentImpact: 4,
    currentLikelihood: 2,
    currentImpact: 3,
    confidence: 'HIGH' as const,
    treatmentStrategy: 'REDUCE' as const,
  });

  // New Control Test Modal
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testForm, setTestForm] = useState({
    controlId: controls[0]?.id || '',
    testType: 'AUTOMATED_CONTINUOUS_TEST' as const,
    period: 'Q3 2026',
    procedure: 'Continuous automated execution of assertion test cases in staging & QA pipeline.',
    evidence: 'LOG-ASSERT-20260828-VERIFIED',
    tester: 'Chief Risk Officer / Control Tester',
    assuranceType: 'SECOND_LINE_REVIEW' as AssuranceType,
    result: 'PASS' as const,
    exceptionsCount: 0,
    conclusion: 'Control tested successfully with 0 exceptions.',
  });

  // Selected Risk for Detail View
  const [selectedRiskId, setSelectedRiskId] = useState<string>(risks[0]?.id || '');
  const selectedRisk = risks.find((r) => r.id === selectedRiskId) || risks[0];

  // Copilot Report State
  const [copilotReport, setCopilotReport] = useState(
    enterpriseRiskEngine.generateAIRiskCopilotReport(selectedRiskId)
  );

  // Handlers
  const handleCreateRisk = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRiskForm.title || !newRiskForm.statementEvent) return;

    const statement = `Because of [${newRiskForm.statementCause}], there is a risk that [${newRiskForm.statementEvent}], which could result in [${newRiskForm.statementImpact}].`;
    const inherentScore = newRiskForm.inherentLikelihood * newRiskForm.inherentImpact;
    const currentScore = newRiskForm.currentLikelihood * newRiskForm.currentImpact;

    const created = enterpriseRiskEngine.createRisk({
      riskCode: newRiskForm.riskCode,
      title: newRiskForm.title,
      statement,
      objectiveId: newRiskForm.objectiveId,
      category: newRiskForm.category,
      causes: [newRiskForm.statementCause],
      riskEvent: newRiskForm.statementEvent,
      impacts: [newRiskForm.statementImpact],
      owner: newRiskForm.owner,
      affectedProducts: [newRiskForm.affectedProducts],
      affectedMarkets: [newRiskForm.affectedMarkets],
      affectedServices: ['Calculation Engine', 'Core APIs'],
      inherentAssessment: {
        likelihood: newRiskForm.inherentLikelihood,
        impact: newRiskForm.inherentImpact,
        score: inherentScore,
        band: inherentScore >= 16 ? 'CRITICAL' : inherentScore >= 12 ? 'HIGH' : inherentScore >= 6 ? 'MODERATE' : 'LOW',
      },
      currentAssessment: {
        likelihood: newRiskForm.currentLikelihood,
        impact: newRiskForm.currentImpact,
        score: currentScore,
        band: currentScore >= 16 ? 'CRITICAL' : currentScore >= 12 ? 'HIGH' : currentScore >= 6 ? 'MODERATE' : 'LOW',
      },
      targetAssessment: { likelihood: 1, impact: 2, score: 2, band: 'LOW' },
      confidence: newRiskForm.confidence,
      appetiteStatus: currentScore > 12 ? 'BREACH' : currentScore > 8 ? 'WATCH' : 'WITHIN_APPETITE',
      treatmentStrategy: newRiskForm.treatmentStrategy,
      status: 'ACTIVE',
    });

    setRisks([...enterpriseRiskEngine.getRisks()]);
    setIsNewRiskModalOpen(false);
    triggerNotification(`Risk [${created.riskCode}] registered successfully. Current Score: ${created.currentAssessment.score}/25`);
  };

  const handleCreateTest = (e: React.FormEvent) => {
    e.preventDefault();
    const ctrl = controls.find((c) => c.id === testForm.controlId);
    if (!ctrl) return;

    const created = enterpriseRiskEngine.createControlTest({
      controlId: testForm.controlId,
      controlCode: ctrl.controlCode,
      testType: testForm.testType,
      period: testForm.period,
      procedure: testForm.procedure,
      evidence: testForm.evidence,
      tester: testForm.tester,
      assuranceType: testForm.assuranceType,
      result: testForm.result,
      exceptionsCount: testForm.exceptionsCount,
      conclusion: testForm.conclusion,
    });

    setControlTests([...enterpriseRiskEngine.getControlTests()]);
    setIsTestModalOpen(false);
    triggerNotification(`Control Test [${created.id}] recorded for ${ctrl.controlCode} - Result: ${created.result}`);
  };

  const handleResolveBreach = (breachId: string) => {
    enterpriseRiskEngine.resolveAppetiteBreach(breachId, 'Resolved via SRE warm-standby container optimization.');
    setAppetiteBreaches([...enterpriseRiskEngine.getAppetiteBreaches()]);
    triggerNotification(`Appetite Breach [${breachId}] marked RESOLVED.`);
  };

  const handleTriageFraudCase = (caseId: string, newStatus: 'INVESTIGATING' | 'SUBSTANTIATED' | 'UNSUBSTANTIATED' | 'CLOSED') => {
    enterpriseRiskEngine.triageFraudCase(caseId, newStatus, `Triaged by Chief Risk Officer. Status updated to ${newStatus}.`);
    setFraudCases([...enterpriseRiskEngine.getFraudCases()]);
    triggerNotification(`Fraud Case [${caseId}] status updated to ${newStatus}`);
  };

  const handleGenerateCopilotReport = (rId?: string) => {
    const r = enterpriseRiskEngine.generateAIRiskCopilotReport(rId);
    setCopilotReport(r);
    triggerNotification('AI Risk Copilot Report refreshed with latest telemetry.');
  };

  // KPIs
  const activeRisksCount = risks.filter((r) => r.status === 'ACTIVE').length;
  const criticalRisksCount = risks.filter((r) => r.currentAssessment.band === 'CRITICAL' || r.currentAssessment.band === 'HIGH').length;
  const breachesCount = appetiteBreaches.filter((b) => b.status !== 'RESOLVED').length;
  const effectiveControlsPct = Math.round(
    (controls.filter((c) => c.operatingStatus === 'EFFECTIVE').length / (controls.length || 1)) * 100
  );
  const normalKrisPct = Math.round(
    (kris.filter((k) => k.thresholdStatus === 'NORMAL').length / (kris.length || 1)) * 100
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#1E293B] font-sans pb-16">
      {/* Top Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-20 shadow-2xs">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#0E7044] text-white flex items-center justify-center shadow-sm">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold tracking-wider text-[#0E7044] uppercase bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200 flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Security Gate 30.9: PASS
                </span>
                <span className="text-xs text-[#64748B] font-mono bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                  ISO 31000:2018 & ISO 22301:2019
                </span>
                <span className="text-xs text-[#64748B] font-mono bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                  Three Lines Model 2026
                </span>
              </div>
              <h1 className="text-xl font-black text-[#0F172A] tracking-tight mt-1">
                Chief Risk Officer (CRO) Command Center & Enterprise Risk OS
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setIsNewRiskModalOpen(true)}
              className="px-4 py-2 bg-[#0E7044] hover:bg-[#0B5C37] text-white rounded-xl text-xs font-bold transition-colors shadow-xs flex items-center gap-2 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Nouveau Risque (Risk)</span>
            </button>

            <button
              onClick={() => setIsTestModalOpen(true)}
              className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl text-xs font-bold transition-colors shadow-xs flex items-center gap-2 cursor-pointer"
            >
              <FileCheck className="w-4 h-4" />
              <span>Tester un Contrôle</span>
            </button>
          </div>
        </div>

        {/* Navigation Sub-Tabs */}
        <div className="max-w-7xl mx-auto px-6 mt-2">
          <div className="flex border-b border-[#E2E8F0] gap-6 overflow-x-auto">
            {[
              { id: 'overview', label: 'Vue Exécutive (CRO)', icon: ShieldAlert },
              { id: 'register', label: `Registre des Risques (${risks.length})`, icon: Layers },
              { id: 'appetite_kri', label: `Appétit & KRIs (${kris.length})`, icon: Activity },
              { id: 'controls', label: `Contrôles & Tests (${controls.length})`, icon: FileCheck },
              { id: 'resilience', label: `Résilience & BCP (${criticalServices.length})`, icon: Zap },
              { id: 'fraud', label: `Risque Fraude & Speak-Up (${fraudCases.length})`, icon: AlertTriangle },
              { id: 'assurance', label: 'Assurance & Audit Interne', icon: Award },
              { id: 'insurance', label: `Assurances & Sinistres (${insurancePolicies.length})`, icon: Building2 },
              { id: 'copilot', label: 'IA Risk Copilot', icon: Sparkles },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`pb-3 text-xs font-bold border-b-2 transition-colors flex items-center gap-2 shrink-0 cursor-pointer ${
                    activeTab === tab.id
                      ? 'border-[#0E7044] text-[#0E7044]'
                      : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-6 mt-8">
        {notificationMsg && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-[#0E7044] rounded-xl text-xs font-bold flex items-center gap-3 animate-fade-in shadow-xs">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <span>{notificationMsg}</span>
          </div>
        )}

        {/* Executive KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white p-4 rounded-xl border border-[#E2E8F0] shadow-2xs">
            <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">Risques Actifs</span>
            <div className="text-xl font-black text-[#0F172A] mt-1">{activeRisksCount} Risques</div>
            <span className="text-[10px] text-amber-700 font-semibold mt-1 block">
              {criticalRisksCount} Élevés / Critiques
            </span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-[#E2E8F0] shadow-2xs">
            <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">Dépassements Appétit</span>
            <div className="text-xl font-black text-amber-600 mt-1">{breachesCount} Alerte</div>
            <span className="text-[10px] text-slate-500 font-medium mt-1 block">Dérogation temporaire active</span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-[#E2E8F0] shadow-2xs">
            <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">Efficacité Contrôles</span>
            <div className="text-xl font-black text-[#0E7044] mt-1">{effectiveControlsPct}%</div>
            <span className="text-[10px] text-emerald-600 font-semibold mt-1 block">Contrôles testés conformes</span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-[#E2E8F0] shadow-2xs">
            <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">Conformité KRIs</span>
            <div className="text-xl font-black text-blue-700 mt-1">{normalKrisPct}%</div>
            <span className="text-[10px] text-blue-600 font-semibold mt-1 block">Seuil NORMAL respecté</span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-[#E2E8F0] shadow-2xs">
            <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">Services Critiques BCP</span>
            <div className="text-xl font-black text-purple-700 mt-1">{criticalServices.length} Services</div>
            <span className="text-[10px] text-purple-600 font-semibold mt-1 block">100% Tests BCP Réussis</span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-[#E2E8F0] shadow-2xs">
            <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">Maturité Audit Interne</span>
            <div className="text-xl font-black text-slate-800 mt-1">{internalAuditProfile.status}</div>
            <span className="text-[10px] text-slate-500 font-medium mt-1 block">Étape 2: 1st/2nd Line Active</span>
          </div>
        </div>

        {/* TAB 1: EXECUTIVE OVERVIEW (CRO) */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Top Enterprise Risk Heatmap / Matrix Summary */}
              <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-2xs">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-base font-black text-[#0F172A]">Matrice d'Exposition aux Risques (Inhérent vs Actuel)</h2>
                    <p className="text-xs text-[#64748B]">
                      Évaluation de l'impact et de la probabilité d'occurrence avant et après contrôles
                    </p>
                  </div>
                  <span className="text-xs font-mono font-bold text-[#0E7044] bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200">
                    Confidence: HIGH
                  </span>
                </div>

                <div className="space-y-4">
                  {risks.map((r) => (
                    <div key={r.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-xs text-[#0E7044] bg-emerald-100 px-2 py-0.5 rounded">
                            {r.riskCode}
                          </span>
                          <span className="font-black text-xs text-[#0F172A]">{r.title}</span>
                        </div>
                        <span
                          className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${
                            r.currentAssessment.band === 'CRITICAL'
                              ? 'bg-red-100 text-red-800'
                              : r.currentAssessment.band === 'HIGH'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-emerald-100 text-emerald-800'
                          }`}
                        >
                          Score Actuel: {r.currentAssessment.score}/25 ({r.currentAssessment.band})
                        </span>
                      </div>

                      <p className="text-xs text-slate-600 font-mono italic">{r.statement}</p>

                      <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
                        <span>Propriétaire (Owner): <strong className="text-slate-800">{r.owner}</strong></span>
                        <span>Inhérent: <strong className="text-slate-700">{r.inherentAssessment.score}/25</strong> &rarr; Cible: <strong className="text-emerald-700">{r.targetAssessment.score}/25</strong></span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Required Box */}
              <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-2xs space-y-4">
                <div className="flex items-center gap-2">
                  <AlertOctagon className="w-5 h-5 text-amber-600" />
                  <h2 className="text-base font-black text-[#0F172A]">Alertes & Actions Prioritaires CRO</h2>
                </div>

                <div className="space-y-3">
                  {appetiteBreaches.map((b) => (
                    <div key={b.id} className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs space-y-1">
                      <div className="flex items-center justify-between font-bold text-amber-900">
                        <span>Dépassement d'Appétit ({b.severity})</span>
                        <span className="text-[10px] bg-amber-200 px-1.5 py-0.5 rounded font-mono">{b.status}</span>
                      </div>
                      <p className="text-amber-800 text-[11px]">{b.breachSummary}</p>
                      {b.status !== 'RESOLVED' && (
                        <button
                          onClick={() => handleResolveBreach(b.id)}
                          className="mt-2 px-3 py-1 bg-amber-800 text-white rounded text-[10px] font-bold cursor-pointer hover:bg-amber-900 transition-colors"
                        >
                          Lever le Dépassement &rarr;
                        </button>
                      )}
                    </div>
                  ))}

                  {controlDeficiencies.map((d) => (
                    <div key={d.id} className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs space-y-1">
                      <div className="flex items-center justify-between font-bold text-[#0F172A]">
                        <span>Déficience Contrôle: {d.controlCode}</span>
                        <span className="text-[10px] bg-red-100 text-red-800 px-1.5 py-0.5 rounded font-mono">{d.severity}</span>
                      </div>
                      <p className="text-slate-600 text-[11px]">{d.description}</p>
                      <div className="text-[10px] text-slate-500">Remédiation visée: {d.targetClosureDate}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: ENTERPRISE RISK REGISTER */}
        {activeTab === 'register' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white p-4 rounded-xl border border-[#E2E8F0]">
              <div className="flex items-center gap-3">
                <Search className="w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher un risque, un code ou un propriétaire..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 w-64 focus:outline-hidden focus:border-[#0E7044]"
                />
              </div>

              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="text-xs font-bold text-slate-600">Catégorie:</span>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-hidden"
                >
                  <option value="ALL">Toutes les catégories</option>
                  <option value="FINANCIAL_CORRECTNESS">FINANCIAL_CORRECTNESS</option>
                  <option value="OPERATIONAL_RESILIENCE">OPERATIONAL_RESILIENCE</option>
                  <option value="CYBERSECURITY">CYBERSECURITY</option>
                  <option value="FRAUD">FRAUD</option>
                </select>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden shadow-2xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-50 border-b border-[#E2E8F0] text-[11px] font-bold text-[#64748B] uppercase tracking-wider">
                      <th className="py-3 px-4">Code & Intitulé du Risque</th>
                      <th className="py-3 px-4">Énoncé Formel (Cause &rarr; Event &rarr; Impact)</th>
                      <th className="py-3 px-4">Catégorie</th>
                      <th className="py-3 px-4">Inhérent</th>
                      <th className="py-3 px-4">Actuel</th>
                      <th className="py-3 px-4">Appétit Status</th>
                      <th className="py-3 px-4">Propriétaire</th>
                      <th className="py-3 px-4">Stratégie Traitement</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0] font-medium">
                    {risks
                      .filter((r) => categoryFilter === 'ALL' || r.category === categoryFilter)
                      .filter((r) => r.title.toLowerCase().includes(searchFilter.toLowerCase()) || r.riskCode.toLowerCase().includes(searchFilter.toLowerCase()))
                      .map((r) => (
                        <tr key={r.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3.5 px-4 font-bold text-[#0F172A]">
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono text-[#0E7044]">{r.riskCode}</span>
                            </div>
                            <div className="text-slate-800 font-bold mt-0.5">{r.title}</div>
                          </td>
                          <td className="py-3.5 px-4 text-slate-600 text-[11px] max-w-md italic">{r.statement}</td>
                          <td className="py-3.5 px-4">
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px] font-mono font-bold">
                              {r.category}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 font-mono font-bold text-slate-500">{r.inherentAssessment.score}/25</td>
                          <td className="py-3.5 px-4 font-mono font-black text-[#0F172A]">
                            <span
                              className={`px-2 py-0.5 rounded ${
                                r.currentAssessment.band === 'CRITICAL'
                                  ? 'bg-red-100 text-red-800'
                                  : r.currentAssessment.band === 'HIGH'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-emerald-100 text-emerald-800'
                              }`}
                            >
                              {r.currentAssessment.score}/25 ({r.currentAssessment.band})
                            </span>
                          </td>
                          <td className="py-3.5 px-4">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                r.appetiteStatus === 'WITHIN_APPETITE'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {r.appetiteStatus}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 text-slate-700 font-semibold">{r.owner}</td>
                          <td className="py-3.5 px-4">
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-800 rounded font-mono font-bold text-[10px]">
                              {r.treatmentStrategy}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: APPÉTIT & KRIS */}
        {activeTab === 'appetite_kri' && (
          <div className="space-y-8">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs space-y-4">
              <h2 className="text-base font-black text-[#0F172A]">Déclarations d'Appétit pour le Risque (Risk Appetite)</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {appetiteStatements.map((app) => (
                  <div key={app.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2 text-xs">
                    <span className="text-[10px] font-bold text-emerald-700 uppercase bg-emerald-50 px-2 py-0.5 rounded">
                      {app.category}
                    </span>
                    <h3 className="font-bold text-[#0F172A] mt-1">{app.statement}</h3>
                    <p className="text-slate-600 text-[11px]">{app.rationale}</p>
                    <div className="text-[10px] text-slate-500 font-mono pt-2 border-t border-slate-200">
                      Tolérance: {app.tolerance}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs space-y-4">
              <h2 className="text-base font-black text-[#0F172A]">Indicateurs Clés de Risque (KRIs - Key Risk Indicators)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {kris.map((kri) => (
                  <div key={kri.id} className="p-4 bg-white border border-slate-200 rounded-xl space-y-2 shadow-2xs">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] font-bold text-[#0E7044] bg-emerald-50 px-2 py-0.5 rounded">
                        {kri.code}
                      </span>
                      <span className="text-[10px] text-emerald-700 font-bold flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> {kri.thresholdStatus}
                      </span>
                    </div>
                    <h3 className="font-bold text-xs text-[#0F172A]">{kri.name}</h3>
                    <div className="text-2xl font-black font-mono text-[#0F172A]">{kri.currentValue}</div>
                    <div className="text-[10px] text-slate-500 font-mono">Seuil Normal: {kri.normalThreshold}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: CONTRÔLES & TESTS */}
        {activeTab === 'controls' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-[#E2E8F0]">
              <h2 className="text-base font-black text-[#0F172A]">Bibliothèque des Contrôles Canoniques (Controls)</h2>
              <button
                onClick={() => setIsTestModalOpen(true)}
                className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl text-xs font-bold transition-colors cursor-pointer flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                <span>Tester un Contrôle</span>
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden shadow-2xs">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-[#E2E8F0] text-[11px] font-bold text-[#64748B] uppercase tracking-wider">
                    <th className="py-3 px-4">Code & Nom du Contrôle</th>
                    <th className="py-3 px-4">Objectif de Contrôle</th>
                    <th className="py-3 px-4">Nature & Type</th>
                    <th className="py-3 px-4">Statut Conception</th>
                    <th className="py-3 px-4">Statut Opérationnel</th>
                    <th className="py-3 px-4">Dernier Test</th>
                    <th className="py-3 px-4">Preuve Exigée</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0] font-medium">
                  {controls.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3.5 px-4 font-bold text-[#0F172A]">
                        <div className="font-mono text-[#0E7044] text-[11px]">{c.controlCode}</div>
                        <div>{c.name}</div>
                      </td>
                      <td className="py-3.5 px-4 text-slate-600 text-[11px] max-w-xs">{c.controlObjective}</td>
                      <td className="py-3.5 px-4">
                        <div className="font-mono text-[10px] text-slate-700">{c.nature} | {c.type}</div>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold text-[10px]">
                          {c.designStatus}
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold text-[10px]">
                          {c.operatingStatus}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 font-mono text-slate-600 text-[11px]">{c.lastTestedAt}</td>
                      <td className="py-3.5 px-4 text-slate-500 text-[10px] italic">{c.evidenceType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 5: RÉSILIENCE & BCP */}
        {activeTab === 'resilience' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs space-y-4">
              <h2 className="text-base font-black text-[#0F172A]">Services Métier Critiques (Critical Business Services)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {criticalServices.map((cbs) => (
                  <div key={cbs.id} className="p-5 bg-white border border-slate-200 rounded-2xl space-y-3 shadow-2xs">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                      <div>
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded uppercase font-mono">
                          {cbs.serviceCode} | {cbs.criticalityTier}
                        </span>
                        <h3 className="text-base font-black text-[#0F172A] mt-1">{cbs.serviceName}</h3>
                      </div>
                      <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 rounded-lg text-xs font-bold font-mono">
                        {cbs.bcpStatus}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                        <span className="text-slate-500 text-[10px] uppercase font-bold block">Target RTO</span>
                        <span className="font-mono font-bold text-[#0F172A]">{cbs.targetRTOHours} Heure(s)</span>
                      </div>
                      <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                        <span className="text-slate-500 text-[10px] uppercase font-bold block">Target RPO</span>
                        <span className="font-mono font-bold text-[#0F172A]">{cbs.targetRPOHours * 60} Minutes</span>
                      </div>
                    </div>

                    <div className="text-xs text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-200">
                      <strong className="text-slate-800 block mb-0.5">Mode Dégradé (Workaround):</strong>
                      {cbs.manualWorkaroundSummary}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 6: FRAUD RISK & SPEAK-UP */}
        {activeTab === 'fraud' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs space-y-4">
              <h2 className="text-base font-black text-[#0F172A]">Gouvernance des Enquêtes de Fraude (Fraud Case Governance)</h2>
              <div className="p-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl text-xs flex items-center gap-2">
                <Lock className="w-4 h-4 shrink-0 text-amber-800" />
                <span>
                  <strong>Accès Restreint :</strong> Les dossiers d'enquête de fraude et les signalements Whistleblowing Speak-Up sont strictement protégés par RBAC.
                </span>
              </div>

              <div className="space-y-4">
                {fraudCases.map((fc) => (
                  <div key={fc.id} className="p-5 bg-white border border-slate-200 rounded-2xl space-y-3 shadow-2xs">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-xs bg-slate-100 text-slate-800 px-2 py-0.5 rounded">
                          {fc.caseCode}
                        </span>
                        <span className="font-bold text-xs text-[#0F172A]">{fc.triggerSource}</span>
                      </div>
                      <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 font-bold rounded text-xs font-mono">
                        {fc.status}
                      </span>
                    </div>

                    <p className="text-xs text-slate-700">{fc.allegationSummary}</p>

                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-1">
                      <span className="text-[10px] font-bold text-slate-500 uppercase block">Journal des Preuves (Evidence Log)</span>
                      {fc.evidenceLog.map((log, idx) => (
                        <div key={idx} className="text-[11px] text-slate-600 flex justify-between font-mono">
                          <span>{log.description}</span>
                          <span className="text-slate-400">{log.timestamp}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 7: ASSURANCE & AUDIT INTERNE */}
        {activeTab === 'assurance' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div>
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded uppercase">
                    IIA Three Lines Governance Model
                  </span>
                  <h2 className="text-xl font-black text-[#0F172A] mt-1">Profil de Maturité Audit Interne</h2>
                </div>
                <span className="px-3 py-1 bg-blue-100 text-blue-900 rounded-lg text-xs font-bold font-mono">
                  Statut: {internalAuditProfile.status}
                </span>
              </div>

              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs space-y-2">
                <p className="text-slate-700 leading-relaxed">{internalAuditProfile.independenceDeclaration}</p>
                <div className="text-[10px] text-slate-500 font-mono">Organe de Tutelle: {internalAuditProfile.governanceBody}</div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 8: ASSURANCES & SINISTRES */}
        {activeTab === 'insurance' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs space-y-4">
              <h2 className="text-base font-black text-[#0F172A]">Registre des Polis d'Assurance & Transfert de Risque</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {insurancePolicies.map((pol) => (
                  <div key={pol.id} className="p-5 bg-white border border-slate-200 rounded-2xl space-y-3 shadow-2xs">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                      <div>
                        <span className="text-[10px] font-bold text-blue-800 bg-blue-50 px-2 py-0.5 rounded uppercase font-mono">
                          {pol.policyType}
                        </span>
                        <h3 className="text-base font-black text-[#0F172A] mt-1">{pol.insurer}</h3>
                      </div>
                      <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 font-bold rounded text-xs font-mono">
                        {pol.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                      <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                        <span className="text-slate-500 text-[10px] uppercase font-bold block">Plafond de Couverture</span>
                        <span className="font-bold text-[#0E7044]">${pol.limitAmountUsd.toLocaleString()} USD</span>
                      </div>
                      <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                        <span className="text-slate-500 text-[10px] uppercase font-bold block">Franchise (Retention)</span>
                        <span className="font-bold text-slate-800">${pol.retentionDeductibleUsd.toLocaleString()} USD</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 9: IA RISK COPILOT */}
        {activeTab === 'copilot' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#0E7044]" />
                  <h2 className="text-base font-black text-[#0F172A]">IA Risk Copilot Analysis & Scenario Synthesizer</h2>
                </div>

                <button
                  onClick={() => handleGenerateCopilotReport(selectedRiskId)}
                  className="px-4 py-2 bg-[#0E7044] hover:bg-[#0B5C37] text-white rounded-xl text-xs font-bold transition-colors cursor-pointer flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Régénérer l'Analyse IA</span>
                </button>
              </div>

              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3 text-xs">
                <div className="font-bold text-[#0F172A] text-sm">{copilotReport.executiveSummary}</div>

                <div className="space-y-1">
                  <span className="font-bold text-slate-700 block">Facteurs Contributifs Majeurs:</span>
                  <ul className="list-disc pl-5 space-y-0.5 text-slate-600">
                    {copilotReport.keyContributingFactors.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-1 pt-2">
                  <span className="font-bold text-slate-700 block">Brouillon d'Analyse de Scénario de Disruption:</span>
                  <p className="text-slate-600 bg-white p-3 rounded-lg border border-slate-200 font-mono text-[11px]">
                    {copilotReport.scenarioAnalysisDraft}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* MODAL 1: NEW RISK REGISTRATION */}
      {isNewRiskModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 border border-slate-200 shadow-xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-lg font-black text-[#0F172A]">Enregistrer un Nouveau Risque Entreprise</h3>
              <button
                onClick={() => setIsNewRiskModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateRisk} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Code Risque</label>
                  <input
                    type="text"
                    value={newRiskForm.riskCode}
                    onChange={(e) => setNewRiskForm({ ...newRiskForm, riskCode: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-mono font-bold"
                    required
                  />
                </div>

                <div>
                  <label className="font-bold text-slate-700 block mb-1">Catégorie</label>
                  <select
                    value={newRiskForm.category}
                    onChange={(e) => setNewRiskForm({ ...newRiskForm, category: e.target.value as any })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold"
                  >
                    <option value="FINANCIAL_CORRECTNESS">FINANCIAL_CORRECTNESS</option>
                    <option value="OPERATIONAL_RESILIENCE">OPERATIONAL_RESILIENCE</option>
                    <option value="CYBERSECURITY">CYBERSECURITY</option>
                    <option value="FRAUD">FRAUD</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Intitulé du Risque</label>
                <input
                  type="text"
                  placeholder="ex: Drift de règles fiscales sur le calcul des cotisations"
                  value={newRiskForm.title}
                  onChange={(e) => setNewRiskForm({ ...newRiskForm, title: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold"
                  required
                />
              </div>

              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                <span className="font-bold text-slate-800 uppercase text-[10px] block">Énoncé Structuré du Risque</span>
                <div>
                  <label className="text-slate-500 text-[10px] block">Cause (Parce que...)</label>
                  <input
                    type="text"
                    placeholder="les barèmes fiscaux évoluent fréquemment..."
                    value={newRiskForm.statementCause}
                    onChange={(e) => setNewRiskForm({ ...newRiskForm, statementCause: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded p-1.5 mt-0.5"
                    required
                  />
                </div>
                <div>
                  <label className="text-slate-500 text-[10px] block">Événement (Il y a un risque que...)</label>
                  <input
                    type="text"
                    placeholder="un calcul publié affiche un écart de cotisations..."
                    value={newRiskForm.statementEvent}
                    onChange={(e) => setNewRiskForm({ ...newRiskForm, statementEvent: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded p-1.5 mt-0.5"
                    required
                  />
                </div>
                <div>
                  <label className="text-slate-500 text-[10px] block">Impact (Ce qui résulterait en...)</label>
                  <input
                    type="text"
                    placeholder="des réclamations clients et un préjudice de réputation..."
                    value={newRiskForm.statementImpact}
                    onChange={(e) => setNewRiskForm({ ...newRiskForm, statementImpact: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded p-1.5 mt-0.5"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Inhérent Likelihood (1-5)</label>
                  <input
                    type="number"
                    min={1}
                    max={5}
                    value={newRiskForm.inherentLikelihood}
                    onChange={(e) => setNewRiskForm({ ...newRiskForm, inherentLikelihood: parseInt(e.target.value) || 1 })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Inhérent Impact (1-5)</label>
                  <input
                    type="number"
                    min={1}
                    max={5}
                    value={newRiskForm.inherentImpact}
                    onChange={(e) => setNewRiskForm({ ...newRiskForm, inherentImpact: parseInt(e.target.value) || 1 })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-mono font-bold"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsNewRiskModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold cursor-pointer"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0E7044] hover:bg-[#0B5C37] text-white rounded-xl font-bold cursor-pointer"
                >
                  Créer le Risque
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: NEW CONTROL TEST */}
      {isTestModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-xl w-full p-6 border border-slate-200 shadow-xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-lg font-black text-[#0F172A]">Consigner un Test de Contrôle</h3>
              <button
                onClick={() => setIsTestModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateTest} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-slate-700 block mb-1">Contrôle à Tester</label>
                <select
                  value={testForm.controlId}
                  onChange={(e) => setTestForm({ ...testForm, controlId: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold"
                >
                  {controls.map((c) => (
                    <option key={c.id} value={c.id}>
                      [{c.controlCode}] {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Résultat du Test</label>
                  <select
                    value={testForm.result}
                    onChange={(e) => setTestForm({ ...testForm, result: e.target.value as any })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold"
                  >
                    <option value="PASS">PASS (Conforme)</option>
                    <option value="QUALIFIED_PASS">QUALIFIED_PASS (Pass sous réserve)</option>
                    <option value="FAIL">FAIL (Échec)</option>
                  </select>
                </div>

                <div>
                  <label className="font-bold text-slate-700 block mb-1">Source Assurance</label>
                  <select
                    value={testForm.assuranceType}
                    onChange={(e) => setTestForm({ ...testForm, assuranceType: e.target.value as any })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold"
                  >
                    <option value="FIRST_LINE_CONTROL_REVIEW">1st Line (Management Self-Test)</option>
                    <option value="SECOND_LINE_REVIEW">2nd Line (Risk & Compliance Review)</option>
                    <option value="INTERNAL_AUDIT">3rd Line (Independent Audit)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Procédure de Test Exécutée</label>
                <textarea
                  rows={2}
                  value={testForm.procedure}
                  onChange={(e) => setTestForm({ ...testForm, procedure: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-medium"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsTestModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold cursor-pointer"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl font-bold cursor-pointer"
                >
                  Enregistrer le Test
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
